import React, { useEffect, useState } from 'react';
import { ExternalLink, RefreshCw, MessageSquare, Filter } from 'lucide-react';
import { apiClient } from '../lib/api';

type Ticket = {
  _id: string;
  zendeskTicketId: number;
  status: string;
  subject: string;
  requesterEmail: string;
  requesterPhysicianId?: string | null;
  organization?: string;
  createdAt: string;
  updatedAt: string;
};

const statusBadgeClass = (status: string): string => {
  switch (status) {
    case 'new': return 'bg-blue-100 text-blue-800';
    case 'open': return 'bg-amber-100 text-amber-800';
    case 'pending': return 'bg-purple-100 text-purple-800';
    case 'hold': return 'bg-gray-100 text-gray-800';
    case 'solved': return 'bg-green-100 text-green-800';
    case 'closed': return 'bg-gray-100 text-gray-700';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const formatRelative = (iso: string): string => {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.round(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
};

export const AdminSupport: React.FC = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [agentBaseUrl, setAgentBaseUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('open');
  const [orgFilter, setOrgFilter] = useState<string>('');

  const load = async () => {
    setError(null);
    const filters: Record<string, string> = {};
    if (statusFilter && statusFilter !== 'open') filters.status = statusFilter;
    if (orgFilter) filters.organization = orgFilter;

    const res = await apiClient.getOpenTickets(filters);
    if (res.success && res.data) {
      setTickets(res.data.tickets);
      setAgentBaseUrl(res.data.agentBaseUrl);
    } else {
      setError(res.error || 'Failed to load tickets');
    }
    setLoading(false);
    setRefreshing(false);
  };

  useEffect(() => {
    load();
    // Poll every 30s so the queue stays fresh while admins watch it.
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [statusFilter, orgFilter]);

  const handleRefresh = () => {
    setRefreshing(true);
    load();
  };

  // Distinct orgs across the loaded tickets, for the filter dropdown.
  const orgs = Array.from(new Set(tickets.map(t => t.organization).filter(Boolean))) as string[];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MessageSquare className="h-6 w-6" />
            Support
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Open tickets across Bloom. Click a row to respond in Zendesk.
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl p-3">
        <Filter className="h-4 w-4 text-gray-500" />
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="text-sm border border-gray-200 rounded px-2 py-1"
        >
          <option value="open">Open queue (new / open / pending / hold)</option>
          <option value="new">New only</option>
          <option value="pending">Pending only</option>
          <option value="solved">Solved</option>
        </select>
        {orgs.length > 0 && (
          <select
            value={orgFilter}
            onChange={e => setOrgFilter(e.target.value)}
            className="text-sm border border-gray-200 rounded px-2 py-1"
          >
            <option value="">All organizations</option>
            {orgs.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        )}
        <span className="ml-auto text-xs text-gray-500">{tickets.length} tickets</span>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-800">
          {error}
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-14 bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : tickets.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-12 text-center text-gray-500">
          <MessageSquare className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p className="font-medium">No tickets matching the filters.</p>
          <p className="text-xs mt-1">When customers email or message support, they'll appear here.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-2.5">Ticket</th>
                <th className="px-4 py-2.5">Subject</th>
                <th className="px-4 py-2.5">Requester</th>
                <th className="px-4 py-2.5">Org</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Updated</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tickets.map(t => (
                <tr key={t._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">#{t.zendeskTicketId}</td>
                  <td className="px-4 py-3 font-medium text-gray-900">{t.subject || '(no subject)'}</td>
                  <td className="px-4 py-3 text-gray-700">{t.requesterEmail}</td>
                  <td className="px-4 py-3 text-gray-600">{t.organization || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusBadgeClass(t.status)}`}>
                      {t.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{formatRelative(t.updatedAt)}</td>
                  <td className="px-4 py-3 text-right">
                    {agentBaseUrl ? (
                      <a
                        href={`${agentBaseUrl}/${t.zendeskTicketId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs font-medium"
                      >
                        Open in Zendesk
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : (
                      <span className="text-xs text-gray-400">No agent URL</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminSupport;
